import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import axios from 'axios';
import type { E55DataResponse } from '../types';

export type TableType = 'pending' | 'inbound' | 'outbound';
export type StateType = 'all' | 'pending' | 'completed';

export interface SortConfig {
  column: string;
  direction: 'ASC' | 'DESC';
}

export interface FilterConfig {
  column: string;
  term: string;
}

interface UseE55DataParams {
  page: number;
  size: number;
  sorts?: SortConfig[];
  filters?: FilterConfig[];
  sort?: string; // Deprecated: use sorts instead
  enabled?: boolean;
  pendingOn?: string;
  accountingYear?: string;
  table?: TableType;
  designatedOperator?: string;
  state?: StateType;
  searchColumn?: string; // Deprecated: use filters instead
  searchTerm?: string; // Deprecated: use filters instead
}

/**
 * Hook to fetch E55 dataset with pagination
 * Supports both legacy and new API structure
 * 
 * @param params Pagination and sorting parameters, and enabled flag for conditional fetching
 * @returns Query result with E55 data
 */
export function useE55Data({ 
  page, 
  size, 
  sorts,
  filters,
  sort, // Legacy support
  pendingOn, 
  accountingYear, 
  table, 
  designatedOperator, 
  state,
  searchColumn, // Legacy support
  searchTerm, // Legacy support
  enabled = true 
}: UseE55DataParams) {
  // Combine filters array with legacy searchColumn/searchTerm for backward compatibility
  const allFilters = useMemo(() => {
    const filterList: FilterConfig[] = filters ? [...filters] : [];
    // Add legacy searchColumn/searchTerm if provided and not already in filters
    if (searchColumn && searchTerm && !filterList.some(f => f.column === searchColumn)) {
      filterList.push({ column: searchColumn, term: searchTerm });
    }
    return filterList;
  }, [filters, searchColumn, searchTerm]);

  // Combine sorts array with legacy sort for backward compatibility
  const allSorts = useMemo(() => {
    const sortList: SortConfig[] = sorts ? [...sorts] : [];
    // Add legacy sort if provided (format: "column,direction")
    if (sort && !sortList.some(s => {
      const [col] = sort.split(',');
      return s.column === col;
    })) {
      const [column, direction] = sort.split(',');
      if (column && (direction === 'ASC' || direction === 'DESC')) {
        sortList.push({ column, direction: direction as 'ASC' | 'DESC' });
      }
    }
    return sortList;
  }, [sorts, sort]);

  return useQuery<E55DataResponse>({
    queryKey: ['e55Data', page, size, allSorts, allFilters, pendingOn, accountingYear, table, designatedOperator, state],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('page', String(page));
      params.append('size', String(size));
      
      // Add multiple sorting parameters if provided
      if (allSorts && allSorts.length > 0) {
        allSorts.forEach((sort) => {
          // Format for backend sorting: "fieldName,direction"
          params.append('sort', `${sort.column},${sort.direction}`);
        });
      }
      
      // Add multiple filter parameters if provided
      if (allFilters && allFilters.length > 0) {
        allFilters.forEach((filter) => {
          if (filter.column && filter.term) {
            params.append(filter.column, filter.term);
          }
        });
      }
      
      if (table) {
        params.append('table', table);
        
        if (state && state !== 'all') {
          params.append('state', state);
        }
        
        if (designatedOperator && designatedOperator !== 'ALL') {
          params.append('designatedOperator', designatedOperator);
        }
        
        if (accountingYear && accountingYear !== 'all') {
          params.append('accountingYear', accountingYear);
        }
      } else {
        if (pendingOn) {
          params.append('pendingOn', pendingOn);
        }
        
        if (accountingYear && accountingYear !== 'all') {
          params.append('accountingYear', accountingYear);
        }
      }

      const response = await axios.get(`/data-set/e55?${params.toString()}`);
      return response.data;
    },
    staleTime: 0, // Set to 0 to ensure fresh data on pagination changes
    gcTime: 5 * 60 * 1000, // Cache for 5 minutes, but will always refetch
    enabled,
    refetchOnMount: true,
    refetchOnWindowFocus: false
  });
} 